import { Link } from 'react-router-dom'

import { FxAlert, FxButton, FxIcon, FxPanel, FxStatusDonut } from '@/components/fx'
import type { DocumentCounts } from '@/types/domain'

import { donutCounts, unclearSummary } from './documentsModel'

/**
 * The three panels above the table: what has been processed, what is waiting
 * for a person, and what can be taken away.
 *
 * Three equal panels in one row, as `Cockpit.dc.html` draws them: a compact
 * 96px ring beside the status lines, then Unklar, then Ergebnisse. An earlier
 * version gave the ring a full-width row of its own at the DS sheet's 240px,
 * which read as a hero the design does not have and pushed everything below
 * it down a screenful. `FxStatusDonut` takes a `size` rather than being
 * rebuilt smaller here.
 *
 * The red panel says "5 Felder in 2 Dokumenten" as drawn: the count endpoint
 * reports `needsReviewFields`, the flagged **fields** across the flagged
 * documents, beside `needsReview`, the documents. The key is optional — the
 * gateway omits it when its aggregate fails — and an absent one means *not
 * counted*, never zero, so the panel then says only what it knows: how many
 * documents. What it still never does is sum the loaded page; that number
 * would change when you turn it.
 */
export function StatusPanels({
  templateId,
  counts,
  isLoading,
  isError = false,
  onRetry,
}: {
  templateId: string
  counts?: DocumentCounts
  isLoading: boolean
  isError?: boolean
  onRetry?: () => void
}) {
  // A failed count is not a count of zero. Drawing the zeroed ring and
  // "Zurzeit wartet kein Feld auf eine Entscheidung." would tell somebody
  // their stack is clear on the strength of a request that never answered —
  // so the panels say what happened instead. (The tab strip and the filter
  // tabs already omit their numbers in this case; this is the same rule.)
  if (isError && counts == null) {
    return (
      <FxPanel className="p-6 sm:p-10">
        <FxAlert tone="danger" title="Die Zahlen konnten nicht geladen werden">
          <p className="m-0">
            Wie viele Dokumente verarbeitet sind und wie viele Felder noch auf eine Entscheidung
            warten, ist gerade nicht abrufbar. Die Dokumente selbst sind davon nicht betroffen.
          </p>
          {onRetry && (
            <FxButton variant="outlined" className="mt-4" onClick={onRetry}>
              Erneut versuchen
            </FxButton>
          )}
        </FxAlert>
      </FxPanel>
    )
  }

  const donut = donutCounts(counts)
  const unclear = counts?.needsReview ?? 0
  const summary = unclearSummary(counts)
  const analysed = donut.done

  // The order and wording the artboard gives these lines. A status with no
  // documents is left out rather than drawn as a zero — the design shows the
  // three that apply to its data, and a column of zeroes is noise.
  const lines: { key: string; icon: string; tone: string; count: number; label: string }[] = [
    { key: 'done', icon: 'check_circle', tone: 'text-success-text', count: donut.done, label: 'analysiert' },
    { key: 'neu', icon: 'refresh', tone: 'text-text-muted', count: donut.neu, label: 'noch nicht verarbeitet' },
    { key: 'inProgress', icon: 'schedule', tone: 'text-warning-text', count: donut.inProgress, label: 'in Verarbeitung' },
    { key: 'unclear', icon: 'error', tone: 'text-danger-text', count: donut.unclear, label: 'unklar' },
  ].filter((line) => line.count > 0)

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <FxPanel className="flex items-center gap-5 p-6">
        {isLoading && counts == null ? (
          <p className="m-0 text-body text-text-muted">Die Zahlen werden geladen …</p>
        ) : (
          <>
            <FxStatusDonut counts={donut} legend={false} size={96} />
            <div className="flex min-w-0 flex-col gap-1.5">
              <h2 className="m-0 text-card-name">Verarbeitung</h2>
              {lines.map((line) => (
                <span key={line.key} className="flex items-center gap-1.5 text-small">
                  <FxIcon name={line.icon} size={18} className={line.tone} />
                  <span className="text-text">
                    <b className="font-semibold">{line.count}</b> {line.label}
                  </span>
                </span>
              ))}
            </div>
          </>
        )}
      </FxPanel>

      <FxPanel accent={unclear > 0 ? 'danger' : 'none'} className="flex flex-col gap-2.5 p-6">
        <h2 className="m-0 text-card-name">Unklar</h2>
        <p className="m-0 flex items-baseline gap-2">
          <b className={`text-[40px] leading-none ${unclear > 0 ? 'text-danger-text' : 'text-text'}`}>
            {summary.value}
          </b>
          <span>{summary.subject}</span>
        </p>
        <span className="flex-grow" />
        {unclear > 0 ? (
          <FxButton variant="dangerTonal" block asChild>
            <Link to={`/templates/${templateId}/unklarheiten`}>Unklarheiten beheben</Link>
          </FxButton>
        ) : (
          <p className="m-0 text-small text-text-muted">
            Zurzeit wartet kein Feld auf eine Entscheidung.
          </p>
        )}
      </FxPanel>

      <FxPanel className="flex flex-col gap-2.5 p-6">
        <h2 className="m-0 text-card-name">Ergebnisse</h2>
        <p className="m-0 flex items-baseline gap-2">
          <b className="text-[40px] leading-none">{analysed}</b>
          <span>Datensätze · Excel</span>
        </p>
        <span className="flex-grow" />
        <FxButton variant="successTonal" block asChild>
          <Link to={`/templates/${templateId}/datensaetze`}>Zu den Datensätzen</Link>
        </FxButton>
      </FxPanel>
    </div>
  )
}
